/**
 * Locale-driven formatting (TMMRSL-0005). Ported from tmm-app TMMAPP-0001. Money
 * arrives as integer minor units and is rendered with Intl in the caller's locale;
 * civil dates (`YYYY-MM-DD`) render as received and are never zone-shifted;
 * instants arrive as ISO-8601 UTC and render in the caller's time zone.
 * Currency is the tenant's (`TenantView.currency`), never derived from the UI language.
 */

import { intlLocale } from './constants';

/** A currency's minor-unit digits, from Intl's own knowledge of the currency (USD → 2, JPY → 0). */
export function minorUnitDigits(currency: string, locale: string): number {
  const digits = new Intl.NumberFormat(intlLocale(locale), {
    style: 'currency',
    currency,
  }).resolvedOptions().maximumFractionDigits;
  // The lib types `maximumFractionDigits` as optional (it is absent for some non-currency
  // notations); a currency format always resolves it, so an absence is a malformed input.
  if (digits === undefined) throw new Error(`Cannot resolve the minor unit of: ${currency}`);
  return digits;
}

/** Divisor for a currency's minor unit (JPY → 1). */
function minorUnitDivisor(locale: string, currency: string): number {
  return 10 ** minorUnitDigits(currency, locale);
}

/**
 * `formatCount(12345, 'en')` → `12,345`; `('fr')` → `12 345` (U+202F or U+00A0). Grouping only —
 * a count is never a money amount.
 */
export function formatCount(n: number, locale: string): string {
  return new Intl.NumberFormat(intlLocale(locale)).format(n);
}

/**
 * A change in a count (new minus churned): `formatSignedCount(3, 'en')` → `+3`,
 * `(-3, 'en')` → `−3`, and zero has no sign (`signDisplay: 'exceptZero'`).
 */
export function formatSignedCount(n: number, locale: string): string {
  return new Intl.NumberFormat(intlLocale(locale), { signDisplay: 'exceptZero' }).format(n);
}

/**
 * `formatMoneyMinor(-123456, 'USD', 'en')` → `-$1,234.56`. `signDisplay: 'exceptZero'` gives a
 * credit its explicit plus sign (`+$12.00`).
 */
export function formatMoneyMinor(
  minor: number,
  currency: string,
  locale: string,
  options: { signDisplay?: 'exceptZero' } = {},
): string {
  const amount = minor / minorUnitDivisor(locale, currency);
  return new Intl.NumberFormat(intlLocale(locale), {
    style: 'currency',
    currency,
    ...options,
  }).format(amount);
}

/**
 * Accounting format (TMMRSL-0075, the rate card worksheet): a negative amount in parentheses —
 * `formatMoneyAccounting(-1234, 'USD', 'en')` → `($12.34)` — through Intl's `currencySign:
 * 'accounting'`, so each locale's own accounting pattern applies (a locale without one keeps its
 * minus sign). Zero and positives read as `formatMoneyMinor`.
 */
export function formatMoneyAccounting(minor: number, currency: string, locale: string): string {
  const amount = minor / minorUnitDivisor(locale, currency);
  return new Intl.NumberFormat(intlLocale(locale), {
    style: 'currency',
    currency,
    currencySign: 'accounting',
  }).format(amount);
}

/**
 * `formatMoneyMicro(1_234_567, 'USD', 'en')` → `$1.23`. Micro = millionths of the major unit
 * (tmm-api's `…Micro` amounts); shown rounded half away from zero to the currency's minor unit.
 */
export function formatMoneyMicro(micro: number, currency: string, locale: string): string {
  const digits = minorUnitDigits(currency, locale);
  const minor = Math.sign(micro) * Math.round(Math.abs(micro) / 10 ** (6 - digits));
  // `|| 0` drops a negative zero (a tiny negative amount), which Intl would show as `-$0.00`.
  return formatMoneyMinor(minor || 0, currency, locale);
}

/**
 * Minor units as a grouping-free amount for a TextField, at the currency's minor unit and
 * round-tripping through `parseMoneyToMinor`: `minorToMoneyInput(2900, 'USD', 'fr')` → `29,00`;
 * JPY keeps no decimals.
 */
export function minorToMoneyInput(minor: number, currency: string, locale: string): string {
  const digits = minorUnitDigits(currency, locale);
  return new Intl.NumberFormat(intlLocale(locale), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    useGrouping: false,
  }).format(minor / 10 ** digits);
}

/**
 * The example amount a money field's message quotes ("like 25.00"), as the field takes it: 2500
 * minor units in its currency, so a zero-decimal currency reads "2500" (TMMRSL-0072). Without a
 * currency (a business that has none set), two decimals.
 */
export function moneyInputExample(currency: string, locale: string): string {
  try {
    return minorToMoneyInput(2500, currency, locale);
  } catch {
    return new Intl.NumberFormat(intlLocale(locale), {
      minimumFractionDigits: 2,
      useGrouping: false,
    }).format(25);
  }
}

/** The locale's group and decimal separators, read from Intl rather than assumed. */
function separators(locale: string): { group: string; decimal: string } {
  const parts = new Intl.NumberFormat(intlLocale(locale)).formatToParts(12345.6);
  return {
    group: parts.find((p) => p.type === 'group')?.value ?? ',',
    decimal: parts.find((p) => p.type === 'decimal')?.value ?? '.',
  };
}

interface ParsedDecimal {
  negative: boolean;
  integer: string;
  fraction: string;
}

/**
 * A typed decimal in the locale's separators: an optional leading `-`, any non-numeric noise
 * (a currency symbol / code, `%`, spaces) stripped, groups dropped. `null` when nothing numeric
 * remains, a `-` sits anywhere but first, or there is more than one decimal separator.
 */
function parseLocaleDecimal(input: string, locale: string): ParsedDecimal | null {
  const { group, decimal } = separators(locale);
  let cleaned = '';
  for (const ch of input) {
    if (/\d/.test(ch) || ch === '-' || ch === group || ch === decimal) cleaned += ch;
  }
  if (cleaned.length === 0) return null;
  const negative = cleaned.startsWith('-');
  if (negative) cleaned = cleaned.slice(1);
  if (cleaned.includes('-')) return null;
  cleaned = cleaned.split(group).join('');
  const pieces = cleaned.split(decimal);
  if (pieces.length > 2) return null;
  const integer = pieces[0] ?? '';
  const fraction = pieces[1] ?? '';
  if (integer.length === 0 && fraction.length === 0) return null;
  return { negative, integer: integer.length === 0 ? '0' : integer, fraction };
}

/**
 * `parseMoneyToMinor('1,234.56', 'USD', 'en')` → `123456`; `('-$50', 'USD', 'en')` → `-5000`;
 * `('1.234,5', 'EUR', 'es')` → `123450`. `null` when unparseable or when more fraction digits
 * than the currency allows are given.
 */
export function parseMoneyToMinor(input: string, currency: string, locale: string): number | null {
  const parsed = parseLocaleDecimal(input, locale);
  if (parsed === null) return null;
  const digits = minorUnitDigits(currency, locale);
  if (parsed.fraction.length > digits) return null;
  const minor = Number(parsed.integer + parsed.fraction.padEnd(digits, '0'));
  if (!Number.isSafeInteger(minor)) return null;
  return parsed.negative ? -minor : minor;
}

/**
 * `bpToPercentText(4200, 'en')` → `42%`; `(1250, 'en')` → `12.5%`; `(1250, 'fr')` → `12,5 %`.
 * Keeps a basis point's precision — up to two decimals, none for a whole value (basis points may
 * exceed 10000 — `12,000%` renders as is).
 */
export function bpToPercentText(bp: number, locale: string): string {
  return new Intl.NumberFormat(intlLocale(locale), {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(bp / 10000);
}

/** `percentToBp('4.25', 'en')` → `425` (two decimals at most; `null` otherwise or when unparseable). */
export function percentToBp(input: string, locale: string): number | null {
  const parsed = parseLocaleDecimal(input, locale);
  if (parsed === null || parsed.fraction.length > 2) return null;
  const bp = Number(parsed.integer + parsed.fraction.padEnd(2, '0'));
  if (!Number.isSafeInteger(bp)) return null;
  return parsed.negative ? -bp : bp;
}

/**
 * A civil date string (`YYYY-MM-DD`) rendered in the locale WITHOUT time-zone shifting: the parts
 * are read into a local-calendar Date (never `new Date('YYYY-MM-DD')`, which parses as UTC and
 * can render the previous day west of Greenwich). Throws on a malformed input.
 */
export function formatCivilDate(
  civil: string,
  locale: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(civil);
  if (match === null) throw new Error(`Not a civil date (YYYY-MM-DD): ${civil}`);
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    throw new Error(`Not a real calendar date: ${civil}`);
  }
  return new Intl.DateTimeFormat(intlLocale(locale), options).format(date);
}

/** `formatCivilMonth`, or `dash` when the value is missing or not a real month. */
export function formatCivilMonthOrDash(
  value: string | null | undefined,
  locale: string,
  dash: string,
): string {
  if (!value) return dash;
  try {
    return formatCivilMonth(value, locale);
  } catch {
    return dash;
  }
}

/**
 * `formatCivilMonth('2026-08-01', 'en')` → `August 2026`. A civil date's month, never
 * zone-shifted.
 */
export function formatCivilMonth(ymd: string, locale: string): string {
  const month = ymd.slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(month)) throw new Error(`Not a civil month (YYYY-MM): ${ymd}`);
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7));
  if (m < 1 || m > 12) throw new Error(`Not a real calendar month: ${ymd}`);
  const options: Intl.DateTimeFormatOptions = { month: 'long', year: 'numeric', timeZone: 'UTC' };
  return new Intl.DateTimeFormat(intlLocale(locale), options).format(
    new Date(Date.UTC(y, m - 1, 1)),
  );
}

/** An ISO-8601 UTC instant rendered in the given IANA time zone. */
export function formatInstant(
  isoUtc: string,
  locale: string,
  timeZone: string,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium', timeStyle: 'short' },
): string {
  const date = new Date(isoUtc);
  if (Number.isNaN(date.getTime())) throw new Error(`Not an instant: ${isoUtc}`);
  return new Intl.DateTimeFormat(intlLocale(locale), { ...options, timeZone }).format(date);
}

const CIVIL_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

/** An instant shown as its day only. */
export const DATE_ONLY: Intl.DateTimeFormatOptions = { dateStyle: 'medium' };

/** The viewer's own IANA time zone, where instants render. */
function localTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * A civil date — or the date part of a longer ISO string — via `formatCivilDate`; `dash` when the
 * value is missing, malformed or not a real calendar date (TMMRSL-0073, DATA-008).
 */
export function formatCivilOrDash(
  value: string | null | undefined,
  locale: string,
  dash: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!value) return dash;
  const day = value.slice(0, 10);
  if (!CIVIL_DAY.test(day)) return dash;
  try {
    return formatCivilDate(day, locale, options);
  } catch {
    return dash;
  }
}

/**
 * An ISO-8601 instant in the viewer's time zone via `formatInstant`; `dash` when the value is
 * missing or doesn't parse (TMMRSL-0073, DATA-008).
 */
export function formatInstantOrDash(
  value: string | null | undefined,
  locale: string,
  dash: string,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (!value) return dash;
  try {
    return formatInstant(value, locale, localTimeZone(), options);
  } catch {
    return dash;
  }
}

/** A civil date as a UTC Date built from its parts (never `new Date('YYYY-MM-DD')`); null if not real. */
function civilUtcDate(value: string): Date | null {
  const match = CIVIL_DAY.exec(value.slice(0, 10));
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) {
    return null;
  }
  return date;
}

/** The words a civil range needs that Intl doesn't supply (the caller's catalog). */
export interface CivilRangeText {
  /** A range with no end: "from {date}", given the formatted start. */
  openEnded: (start: string) => string;
  /** A range with an end and no start: "until {date}", given the formatted end. Without it, the dash. */
  endOnly?: (end: string) => string;
  /** A range whose start is missing or malformed. */
  dash: string;
}

/**
 * Two civil dates as one range through `Intl.DateTimeFormat.prototype.formatRange`, so shared parts
 * collapse ("Sep 1 – 30, 2026", "1–30 sept 2026"); the dates are built in UTC from their parts and
 * rendered in UTC, never zone-shifted. A missing or malformed end is the open-ended text with the
 * start (TMMRSL-0073, DATA-008); a missing or malformed start with a good end is `endOnly` with the
 * end (TMMRSL-0075), or the dash without it.
 */
export function formatCivilRange(
  from: string | null | undefined,
  to: string | null | undefined,
  locale: string,
  text: CivilRangeText,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string {
  const format = new Intl.DateTimeFormat(intlLocale(locale), { ...options, timeZone: 'UTC' });
  const start = from ? civilUtcDate(from) : null;
  const end = to ? civilUtcDate(to) : null;
  if (start === null) {
    return end !== null && text.endOnly ? text.endOnly(format.format(end)) : text.dash;
  }
  if (end === null) return text.openEnded(format.format(start));
  return format.formatRange(start, end);
}

/**
 * Intl names these, but they are not ISO 3166-1 alpha-2 countries (CLDR regions and the
 * unknown-region code). Stripe refuses them. No full country list is kept here.
 */
const NON_COUNTRY_REGIONS = new Set(['EU', 'EZ', 'UN', 'QO', 'XA', 'XB', 'ZZ']);

/**
 * A two-letter region code's name in the locale through `Intl.DisplayNames` (`US` → "United
 * States"); `null` for anything Intl doesn't name, and for the non-country codes above.
 * TMMRSL-0073: the Stripe Connect country hint; no country list is kept here.
 */
export function regionName(code: string, locale: string): string | null {
  if (!/^[A-Z]{2}$/.test(code) || NON_COUNTRY_REGIONS.has(code)) return null;
  try {
    const name = new Intl.DisplayNames([intlLocale(locale)], { type: 'region', fallback: 'none' }).of(
      code,
    );
    return name ?? null;
  } catch {
    return null;
  }
}

/**
 * The first candidate that is a non-empty string — the display currency. `''` and `null`/`undefined`
 * are skipped alike, so a server-sent empty currency falls back instead of reaching
 * `Intl.NumberFormat` (which throws on ''). Returns '' when every candidate is empty; callers that
 * format must treat '' as "no currency".
 */
export function firstCurrency(...candidates: Array<string | null | undefined>): string {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate !== '') return candidate;
  }
  return '';
}
